// MIRROR of apps/web/src/components/meeting/presenters/CharacterPresenter.tsx — keep in sync.
// apps/landing cannot import from apps/web. The wire drawing (clipPath, posture, glow) is the
// source's, with TWO intentional divergences: (1) the wire is ember in every state (the meeting
// presenter's zinc default belongs to the meeting room); (2) the eyes are scaled up — the source draws
// them at eyeR 0.10 of a 0.30·S unit, which is ~2px at the 64–88px the landing renders, i.e. invisible.
// Here the character fills its box (scale 0.55·S instead of 0.30·S, so an 88px-tall clip is an 88px
// clip) and the rendered eye is ≥ 5.5px radius with a 1px ink outline so it reads on the wash and on
// a frame (≥ 7.5px radius, ≥ 3px pupil, so a row above the pupil carries a ≥ 10px white run). On top of the mirror the landing adds the `eyes` vocabulary (look / wide / squint / sparkle /
// double-blink / half-lid / glance / exit / wink), a `persona` (skeptic lids, lean, breathing period)
// and `facing`; these are marked LANDING-ONLY below. The source's own header follows.
/**
 * CharacterPresenter — the Clippy-shaped option, for comparison.
 *
 * Same state machine as the abstract presenter, different skin: a paperclip
 * drawn as a single continuous wire path, with eyes. Speaking bob is driven by
 * the same real RMS, so the two variants are honestly comparable — the only
 * thing that changes is the form.
 *
 * Read the trade before shipping this: a character is memorable and warm, but
 * Clippy is cultural shorthand for "assistant that interrupts you", which is a
 * live risk when the whole pitch is an AI that runs your presales calls. Kept
 * as a real option, not the default.
 */
import { useRef } from 'react';
import { useCanvasLoop } from './useCanvasLoop';
import { EyeRig, drawEyes, type Eyes } from './eyes';

/** Identical to apps/web/src/components/meeting/usePresenterState.ts — keep in sync. */
export type PresenterState = 'speaking' | 'thinking' | 'listening' | 'muted' | 'offline';

/** The landing has no audio: `state` and `levelRef` come from the ledger step, not from an analyser. */
export interface PresenterProps {
  state: PresenterState;
  /** 0..1 loudness, read every frame. Synthetic on the placeholder session. */
  levelRef: React.RefObject<number>;
  /** Square size in CSS pixels. */
  size: number;
  /** LANDING-ONLY: the eye vocabulary; transitions tween at 180–280ms on the --ease-out curve. */
  eyes?: Eyes;
  /** LANDING-ONLY: character traits that bias the drawing. */
  persona?: Persona;
  /** LANDING-ONLY: mirror horizontally so the character faces right. */
  facing?: 'left' | 'right';
}

// ── LANDING-ONLY: eyes and persona ──
export type { Eyes, EyeMode } from './eyes';
export interface Persona {
  /** 0..1: how far the lids sit down at rest (the skeptic). */
  skeptic?: number;
  /** Resting lean in degrees (negative leans back). */
  lean?: number;
  /** Breathing period in seconds. */
  breath?: number;
}
// ───────────────────────────────────────────────────────────────────────────────────────────────────

const ACCENT = '#E8530A';
// DIVERGENCE 1: on the landing the wire is ember in every state (the meeting presenter's zinc default
// belongs to the meeting room). Dim is ember at 55%.
const WIRE = '#E8530A';
const WIRE_DIM = 'rgba(232,83,10,0.55)';

/** One continuous paperclip wire in unit space (x -0.5..0.5, y -0.8..0.8). */
function clipPath(ctx: CanvasRenderingContext2D) {
  ctx.beginPath();
  ctx.moveTo(0.32, -0.45);
  ctx.lineTo(0.32, 0.42);
  ctx.arc(0, 0.42, 0.32, 0, Math.PI);            // outer bottom turn
  ctx.lineTo(-0.32, -0.52);
  ctx.arc(-0.11, -0.52, 0.21, Math.PI, Math.PI * 2); // top turn
  ctx.lineTo(0.10, 0.22);
  ctx.arc(0, 0.22, 0.10, 0, Math.PI);            // inner bottom turn
  ctx.lineTo(-0.10, -0.28);
}

export function CharacterPresenter({ state, levelRef, size, eyes, persona, facing }: PresenterProps) {
  const blinkRef = { current: 0 };
  // LANDING-ONLY: under reduced motion the loop does not run, so eye changes must re-run the draw.
  const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  // LANDING-ONLY: the shared eye rig (tweens, blink schedule, one-shots) persists across draws.
  const rig = useRef(new EyeRig());

  const canvasRef = useCanvasLoop(({ ctx, w, h, t, reduced }) => {
    const now = performance.now();
    const breath = persona?.breath ?? 3;
    const eyeFrame = rig.current.update(eyes, persona?.skeptic ?? 0, t, now, reduced);
    const blinking = eyeFrame.blinking;
    // ── end LANDING-ONLY ──
    const cx = w / 2;
    const cy = h / 2;
    const S = Math.min(w, h);
    const scale = S * 0.55; // DIVERGENCE 2 (size): the clip fills its box; the source uses 0.30
    const level = state === 'speaking' ? (levelRef.current ?? 0) : 0;

    const speaking = state === 'speaking';
    const grey = state === 'muted' || state === 'offline';
    const wire = speaking ? ACCENT : grey ? WIRE_DIM : WIRE;

    // Posture per state: bob on voice, lean while working, droop when gone.
    let tilt = 0;
    let bob = 0;
    if (reduced) {
      tilt = state === 'thinking' ? -0.20 : state === 'offline' ? 0.24 : 0;
    } else if (speaking) {
      bob = -level * S * 0.035;
      tilt = Math.sin(t * 9) * 0.05 * level;
    } else if (state === 'thinking') {
      tilt = -0.20 + Math.sin(t * 2) * 0.02;
    } else if (state === 'offline') {
      tilt = 0.24;
    } else {
      tilt = Math.sin(t * 0.9) * 0.05;             // idle sway
      bob = Math.sin(t * 0.9) * S * 0.004;
    }

    ctx.save();
    ctx.translate(cx, cy + bob);
    // LANDING-ONLY: persona lean, mirrored facing, idle breathing (3% amplitude, never more).
    ctx.rotate(tilt + ((persona?.lean ?? 0) * Math.PI) / 180);
    if (facing === 'right') ctx.scale(-1, 1);
    const breathe = reduced ? 1 : 1 + 0.015 * Math.sin((t / breath) * Math.PI * 2); // 3% peak to peak, about rest
    ctx.scale(scale * breathe, scale * breathe);

    if (speaking && level > 0.03) {
      ctx.save();
      ctx.shadowColor = `rgba(232,83,10,${0.5 * level})`;
      ctx.shadowBlur = 26 * level;
      ctx.lineWidth = 0.115;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = wire;
      clipPath(ctx);
      ctx.stroke();
      ctx.restore();
    }
    ctx.lineWidth = 0.115;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = wire;
    clipPath(ctx);
    ctx.stroke();

    // ── eyes ── (DIVERGENCE 2: ≥ 7.5px radius, 1px ink outline; the LANDING-ONLY vocabulary via eyes.ts)
    const blink = blinking;
    const closed = state === 'offline' || state === 'muted';
    const lookUp = state === 'thinking' ? -0.035 : 0;
    const eyeR = Math.max(0.1, 7.5 / scale) + (speaking ? level * 0.015 : 0);
    const sep = Math.max(0.125, eyeR * 1.15);
    const px1 = 1 / scale; // one CSS pixel in unit space
    drawEyes(ctx, { xs: [-sep, sep], y: -0.40, r: eyeR, px: px1, lidColor: grey ? WIRE_DIM : WIRE, mirrored: facing === 'right', lookUp, closed, dim: grey }, eyeFrame);
    ctx.canvas.dataset.pupil = `${Math.abs(eyeFrame.px).toFixed(2)},${Math.abs(eyeFrame.py).toFixed(2)}`;
    ctx.restore();
    blinkRef.current = blink ? 1 : 0;

    // ── thinking: three dots orbiting above the head ──
    if (state === 'thinking') {
      for (let i = 0; i < 3; i++) {
        const p = reduced ? i * 0.33 : ((t * 0.7 + i * 0.28) % 1);
        const a = -Math.PI / 2 + (p - 0.5) * 1.5;
        const r = S * 0.30;
        ctx.beginPath();
        ctx.arc(cx + Math.cos(a) * r, cy - S * 0.20 + Math.sin(a) * r * 0.35, S * 0.011, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(232,83,10,${0.35 + 0.5 * Math.sin(p * Math.PI)})`;
        ctx.fill();
      }
    }

    // Muted needs no mark here either — see the note in AbstractPresenter. The
    // character just goes grey with its eyes shut, which reads as "not hearing
    // you" without claiming the AGENT is the one muted.
  }, [state, reduced ? `${eyes?.mode}/${eyes?.key}/${facing}` : '']);

  return <canvas ref={canvasRef} width={size} height={size} style={{ width: size, height: size, display: 'block' }} aria-hidden="true" />;
}
